import { useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { MenuItem, Typography } from '@mui/material';

import { appendInputIdToLabel } from 'utils';
import { useChoiceOptions } from 'context/choice-randomization';
import { defaultLanguage, useTranslationContext } from 'context';
import {
  FormChoiceResponse,
  FormChoiceResponseType,
  FORM_OTHER_OPTION_VALUE,
} from 'types';

import { Input } from '../input';
import { InputField, AutocompleteField } from '../fields';

import { FormSelectFieldProps } from './types';

export const FormSelectField = ({
  id,
  label,
  inputId,
  options,
  placeholder,
  isSearchable,
  translations,
  labelOutside = true,
}: FormSelectFieldProps) => {
  const { language } = useTranslationContext();
  const { t } = useTranslation('common');
  const otherLabel = t('choiceOther');
  const [field, , helpers] = useField<
    FormChoiceResponse | FormChoiceResponse[] | null
  >(id);
  const responses = Array.isArray(field.value)
    ? field.value
    : field.value
      ? [field.value]
      : [];
  const otherResponse = responses.find(
    (response) => response.type === FormChoiceResponseType.OTHER
  );
  const controlValue = responses.map((response) =>
    response.type === FormChoiceResponseType.OTHER
      ? FORM_OTHER_OPTION_VALUE
      : response.value
  );

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        placeholder,
        label: appendInputIdToLabel(label, inputId),
        options: options?.options
          ? options?.options.map((value) => ({ value, label: value }))
          : [],
      };
    }

    const translation = translations[language];
    const langOptions = translation?.options || options?.options;
    return {
      placeholder: translation?.placeholder || placeholder,
      label: appendInputIdToLabel(
        label ? translation?.label || label : label,
        inputId
      ),
      options: langOptions
        ? langOptions.map((value, index) => ({
            label: value,
            value: options?.options?.[index] || value,
          }))
        : [],
    };
  }, [label, placeholder, options?.options, language, translations, inputId]);

  const orderedOptions = useChoiceOptions(
    id,
    options?.randomize,
    translation.options
  );

  // Other needs a separate free-text input; searchable mode supports fixed options only.
  const select =
    isSearchable && !options?.multiple && !options?.allowOther ? (
      <AutocompleteField
        name={id}
        options={orderedOptions}
        label={translation.label}
        displayValue={controlValue[0]}
        placeholder={translation.placeholder}
        mapValue={(value) =>
          value ? { value, type: FormChoiceResponseType.OPTION } : null
        }
      />
    ) : (
      <InputField
        select
        name={id}
        fullWidth
        className="selectInput"
        label={translation.label}
        labelOutside={labelOutside}
        placeholder={translation.placeholder}
        displayValue={
          options?.multiple ? controlValue : (controlValue[0] ?? '')
        }
        mapValue={(selectedValue) => {
          const selectedValues = Array.isArray(selectedValue)
            ? selectedValue
            : [selectedValue];
          const nextResponses = selectedValues.map((value) =>
            value === FORM_OTHER_OPTION_VALUE
              ? {
                  value: otherResponse?.value ?? '',
                  type: FormChoiceResponseType.OTHER,
                }
              : { value, type: FormChoiceResponseType.OPTION }
          );
          return options?.multiple ? nextResponses : (nextResponses[0] ?? null);
        }}
        slotProps={{
          select: {
            multiple: options?.multiple,
            renderValue: (selected) => {
              const values = Array.isArray(selected) ? selected : [selected];
              if (values.length === 0 || selected === '') {
                return (
                  <Typography component="span" sx={{ color: 'text.disabled' }}>
                    {translation.placeholder}
                  </Typography>
                );
              }
              return values
                .map((value) =>
                  value === FORM_OTHER_OPTION_VALUE
                    ? otherLabel
                    : (orderedOptions.find((option) => option.value === value)
                        ?.label ?? value)
                )
                .join(', ');
            },
          },
        }}
      >
        {orderedOptions &&
          orderedOptions.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        {options?.allowOther && (
          <MenuItem value={FORM_OTHER_OPTION_VALUE}>{otherLabel}</MenuItem>
        )}
      </InputField>
    );

  return (
    <div className="flex flex-col gap-2">
      {select}
      {options?.allowOther && otherResponse && (
        <Input
          fullWidth
          label={otherLabel}
          value={otherResponse.value}
          placeholder={t('choiceOtherPlaceholder')}
          onBlur={() => helpers.setTouched(true, true)}
          onChange={(event) => {
            const nextOtherResponse: FormChoiceResponse = {
              value: event.target.value,
              type: FormChoiceResponseType.OTHER,
            };
            helpers.setValue(
              Array.isArray(field.value)
                ? [
                    ...field.value.filter(
                      (response) =>
                        response.type !== FormChoiceResponseType.OTHER
                    ),
                    nextOtherResponse,
                  ]
                : nextOtherResponse,
              true
            );
          }}
        />
      )}
    </div>
  );
};
